import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataGrid } from '../../components/DataGrid.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/** T103VendorSlaTracking (GET /v1/vendor/sla, §21). */
interface SlaRow {
  readonly slaId: string;
  readonly jobRequestId: string;
  readonly bucket: string;
  readonly startedAt: string;
  readonly dueAt: string;
  readonly breachedAt: string | null;
  readonly status: string;
}

const STATUSES = ['', 'OPEN', 'PAUSED', 'BREACHED', 'CLOSED'] as const;

export function VendorSlaPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<readonly SlaRow[] | null>(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setRows(null);
      try {
        const qs = status ? `?status=${encodeURIComponent(status)}` : '';
        const r = await apiRequest<{ items: SlaRow[] }>(`/v1/vendor/sla${qs}`, { token });
        if (alive) {
          setRows(r.items);
          setError(null);
        }
      } catch (e) {
        if (alive) {
          setError((e as Error).message);
          setRows([]);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, status]);

  return (
    <main aria-label="vendor-sla">
      <h1>{t('vops.sla.title')}</h1>

      <label htmlFor="sla-status">{t('vops.common.status')}</label>
      <select id="sla-status" aria-label="sla-status" value={status} onChange={(e) => setStatus(e.target.value)}>
        {STATUSES.map((s) => (
          <option key={s || 'ALL'} value={s}>{s || t('vops.common.all')}</option>
        ))}
      </select>

      {error ? <p role="alert">{error}</p> : null}

      {rows === null ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p aria-label="sla-empty">{t('vops.sla.empty')}</p>
      ) : (
        <DataGrid<SlaRow>
          caption="sla-table"
          rows={rows}
          getRowId={(r) => r.slaId}
          rowAriaLabel={(r) => `sla-${r.slaId}`}
          columns={[
            { key: 'job', header: t('vops.sla.colJob'), value: (r) => r.jobRequestId },
            { key: 'bucket', header: t('vops.sla.colBucket'), value: (r) => r.bucket },
            { key: 'status', header: t('vops.common.status'), value: (r) => r.status, render: (r) => <span aria-label={`sla-status-${r.slaId}`}>{r.status}</span> },
            { key: 'started', header: t('vops.sla.colStarted'), value: (r) => at(r.startedAt) },
            { key: 'due', header: t('vops.sla.colDue'), value: (r) => at(r.dueAt) },
            { key: 'breached', header: t('vops.sla.colBreached'), value: (r) => (r.breachedAt ? at(r.breachedAt) : '—') },
          ]}
        />
      )}
    </main>
  );
}
